import type { BillingSubscriptionPriceKey } from '../types/billing';
import { CookieService } from '../services/cookies';
import { isStripePriceId } from '../config/paymentLinkSeo';
import { queryParams } from './queryParams';
import { isBillingSubscriptionPriceKey } from './billingSubscriptionPlans';

export { isBillingSubscriptionPriceKey } from './billingSubscriptionPlans';

const STORAGE_KEY = 'palen.billing.selected_subscription';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const FALLBACK_COOKIE = 'palenPaymentLinkSelection';

type StoredBillingPlan = {
  priceKey: BillingSubscriptionPriceKey;
  selectedAt: number;
  source?: 'payment_link';
  priceId?: string;
  language?: 'en' | 'fr';
};
let transientSelection: StoredBillingPlan | null = null;

export function saveBillingPlanSelection(priceKey: BillingSubscriptionPriceKey, source?: 'payment_link', priceId?: string, language?: 'en' | 'fr'): void {
  if (!isBillingSubscriptionPriceKey(priceKey) || (source && !isStripePriceId(priceId))) return;

  const selection: StoredBillingPlan = { priceKey, selectedAt: Date.now(), ...(source ? { source, priceId, language } : {}) };
  transientSelection = source ? selection : null;
  CookieService.remove(FALLBACK_COOKIE);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    transientSelection = null;
  } catch {
    if (source) CookieService.set(FALLBACK_COOKIE, encodeURIComponent(JSON.stringify(selection)), 1);
  }
}

export function readBillingPlanSelection(): BillingSubscriptionPriceKey | null {
  return readStoredBillingPlan()?.priceKey ?? null;
}

export function readPaymentLinkSelection(): BillingSubscriptionPriceKey | null {
  const selection = readStoredBillingPlan();
  return selection?.source === 'payment_link' ? selection.priceKey : null;
}

export function readPaymentLinkPriceSelection(): string | null {
  const urlSelection = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('paymentLinkPriceId');
  if (isStripePriceId(urlSelection)) return urlSelection;
  const selection = readStoredBillingPlan();
  return selection?.source === 'payment_link' ? selection.priceId ?? null : null;
}

export function readPaymentLinkLanguageSelection(): 'en' | 'fr' | null {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const language = params.get('paymentLinkLanguage');
    if (isStripePriceId(params.get('paymentLinkPriceId')) && (language === 'en' || language === 'fr')) return language;
  }
  const selection = readStoredBillingPlan();
  return selection?.source === 'payment_link' ? selection.language ?? null : null;
}

export function readPaymentLinkAuthLanguageSelection(): 'en' | 'fr' | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const isVerification = queryParams.has('sessionId') && queryParams.has('token');
  const isPaymentRegistration = /^\/(?:login|register)\/?$/.test(window.location.pathname)
    && isStripePriceId(params.get('paymentLinkPriceId'));
  return isVerification || isPaymentRegistration ? readPaymentLinkLanguageSelection() : null;
}

function readStoredBillingPlan(): StoredBillingPlan | null {
  try {
    let rawSelection: string | null = null;
    try { rawSelection = window.localStorage.getItem(STORAGE_KEY); } catch { /* The essential cookie preserves redirect continuity when local storage is blocked. */ }
    const fallbackCookie = CookieService.get(FALLBACK_COOKIE);
    rawSelection = fallbackCookie ? decodeURIComponent(fallbackCookie) : transientSelection ? JSON.stringify(transientSelection) : rawSelection;
    if (!rawSelection) return null;

    const selection = JSON.parse(rawSelection) as Partial<StoredBillingPlan>;
    const isValid = typeof selection.selectedAt === 'number'
      && isBillingSubscriptionPriceKey(selection.priceKey)
      && (selection.source === undefined || selection.source === 'payment_link')
      && (selection.source !== 'payment_link' || isStripePriceId(selection.priceId))
      && (selection.language === undefined || selection.language === 'en' || selection.language === 'fr')
      && selection.selectedAt <= Date.now()
      && Date.now() - selection.selectedAt <= MAX_AGE_MS;

    if (isValid) return selection as StoredBillingPlan;
    clearBillingPlanSelection();
  } catch {
    clearBillingPlanSelection();
  }

  return null;
}

export function clearBillingPlanSelection(): void {
  transientSelection = null;
  CookieService.remove(FALLBACK_COOKIE);
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing else is required when browser storage is unavailable.
  }
}
